'use client';

/**
 * AI Feature Cards — 3×2 grid showcasing what makes Locobuzz AI different.
 * Each card has a title with bolded accent word, description, and a custom
 * inline SVG illustration in the Verlynk-reference visual style.
 *
 * Used on /ai-stack page (replaces "What Makes AI Different" section).
 * All illustrations are self-contained SVG — no external assets needed.
 */
import React from 'react';
import { useTranslation } from "@/hooks/useTranslation";
const FEATURES = [{
  key: 'workflow',
  colorClass: 'aistk-feat-card--c1',
  titlePre: 'Built Into Every',
  titleAccent: 'Workflow.',
  desc: 'Trained on billions of real customer support interactions. No plugins. No disconnected insight. Action happens where the signal is.',
  Illu: WorkflowIllu
}, {
  key: 'privacy',
  colorClass: 'aistk-feat-card--c2',
  titlePre: 'Privacy-First by',
  titleAccent: 'Design.',
  desc: 'ISO 27001:2022 · SOC 2 Type II · GDPR-ready. Enterprise-grade data security at every layer, built for regulated industries.',
  Illu: PrivacyIllu
}, {
  key: 'language',
  colorClass: 'aistk-feat-card--c3',
  titlePre: 'CX-Trained Language',
  titleAccent: 'Models.',
  desc: 'Understands industry-specific language, regional nuance, abbreviations, and emotional context far beyond what general-purpose LLMs can offer.',
  Illu: LanguageIllu
}, {
  key: 'intent',
  colorClass: 'aistk-feat-card--c4',
  titlePre: 'Intent & Emotion',
  titleAccent: 'Intelligence.',
  desc: 'Detects urgency, frustration, loyalty signals, and buying intent in every message. Not just the words, but the meaning behind them.',
  Illu: IntentIllu
}, {
  key: 'topics',
  colorClass: 'aistk-feat-card--c5',
  titlePre: 'Topic, Theme &',
  titleAccent: 'Entity Detection.',
  desc: 'Surfaces trending complaints, brand mention analysis, and sentiment shifts before they become patterns worth worrying about.',
  Illu: TopicsIllu
}, {
  key: 'predictive',
  colorClass: 'aistk-feat-card--c6',
  titlePre: 'Generative +',
  titleAccent: 'Predictive.',
  desc: 'Drafts smarter responses. Flags risks early. Predicts CSAT drops and churn signals before they show up in your QBR.',
  Illu: PredictiveIllu
}];
export default function AIFeatureCards() {
  const {
    t
  } = useTranslation();
  return <section className="aistk-feats" id="different">
      <div className="aistk-feats__bg" aria-hidden="true" />
      <div className="container aistk-feats__inner">
        <div className="aistk-feats__header">
          <h2 className="aistk-feats__title">
            {t("aifeaturecards.native")} <span className="aistk-feats__title-grad">{t("aifeaturecards.ai")}</span> {t("aifeaturecards.not_bolted_on")}
          </h2>
          <p className="aistk-feats__sub">
            {t("aifeaturecards.the_difference_between_ai_that_sits")}
          </p>
        </div>

        <div className="aistk-feats__grid">
          {FEATURES.map(({
          key,
          colorClass,
          titlePre,
          titleAccent,
          desc,
          Illu
        }) => <article key={key} className={`aistk-feat-card ${colorClass}`}>
              <div className="aistk-feat-card__text">
                <h3 className="aistk-feat-card__title">
                  {titlePre} <strong>{titleAccent}</strong>
                </h3>
                <p className="aistk-feat-card__desc">{desc}</p>
              </div>
              <div className="aistk-feat-card__illu" aria-hidden="true">
                <Illu />
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}

/* ───────────────────────────────────────────────────────────
   SVG ILLUSTRATIONS — 1 per card
   Style: soft pastel, glass-card, light blue/purple
   Each ~240×180, viewBox responsive
   ─────────────────────────────────────────────────────────── */

/* 1. Workflow — connected nodes with flowing lines */
function WorkflowIllu() {
  const {
    t
  } = useTranslation();
  return <svg viewBox="0 0 240 180" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="wfNode" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7AA9FF" />
          <stop offset="100%" stopColor="#5232FF" />
        </linearGradient>
        <linearGradient id="wfNode2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#A1B8FF" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#7E6BFF" stopOpacity="0.9" />
        </linearGradient>
      </defs>

      {/* Connecting lines */}
      <g stroke="#5232FF" strokeWidth="1.2" fill="none" opacity="0.35" strokeDasharray="3 3">
        <path d="M 50 90 Q 100 50, 150 90" />
        <path d="M 50 90 Q 100 130, 150 90" />
        <path d="M 150 90 L 200 60" />
        <path d="M 150 90 L 200 120" />
      </g>

      {/* Floating cards/nodes */}
      <g className="aistk-feat-illu__float-a">
        <rect x="20" y="76" width="56" height="28" rx="8" fill="white" stroke="rgba(82,50,255,0.18)" />
        <rect x="28" y="84" width="8" height="3" rx="1.5" fill="#5232FF" opacity="0.7" />
        <rect x="28" y="90" width="32" height="2" rx="1" fill="#5232FF" opacity="0.25" />
        <rect x="28" y="95" width="22" height="2" rx="1" fill="#5232FF" opacity="0.25" />
      </g>

      <g className="aistk-feat-illu__float-b">
        <circle cx="150" cy="90" r="22" fill="url(#wfNode)" opacity="0.92" />
        <circle cx="150" cy="90" r="14" fill="white" opacity="0.9" />
        <circle cx="150" cy="90" r="6" fill="url(#wfNode)" />
      </g>

      <g className="aistk-feat-illu__float-c">
        <rect x="180" y="44" width="50" height="32" rx="8" fill="white" stroke="rgba(82,50,255,0.18)" />
        <circle cx="192" cy="60" r="4" fill="#FF9D6B" />
        <rect x="201" y="55" width="22" height="2" rx="1" fill="#5232FF" opacity="0.30" />
        <rect x="201" y="61" width="18" height="2" rx="1" fill="#5232FF" opacity="0.25" />
      </g>

      <g className="aistk-feat-illu__float-a">
        <rect x="180" y="106" width="50" height="32" rx="8" fill="white" stroke="rgba(82,50,255,0.18)" />
        <circle cx="192" cy="122" r="4" fill="#5232FF" opacity="0.7" />
        <rect x="201" y="117" width="22" height="2" rx="1" fill="#5232FF" opacity="0.30" />
        <rect x="201" y="123" width="18" height="2" rx="1" fill="#5232FF" opacity="0.25" />
      </g>

      {/* Pulse rings */}
      <g className="aistk-feat-illu__pulse">
        <circle cx="150" cy="90" r="32" fill="none" stroke="#5232FF" strokeWidth="0.8" opacity="0.3" />
        <circle cx="150" cy="90" r="40" fill="none" stroke="#0077FF" strokeWidth="0.6" opacity="0.18" />
      </g>
    </svg>;
}

/* 2. Privacy — lock + shield + compliance badges */
function PrivacyIllu() {
  const {
    t
  } = useTranslation();
  return <svg viewBox="0 0 240 180" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="shieldGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#A1B8FF" />
          <stop offset="100%" stopColor="#5232FF" />
        </linearGradient>
      </defs>

      {/* Central shield */}
      <g className="aistk-feat-illu__float-a">
        <path d="M 120 30 L 90 44 L 90 86 Q 90 116, 120 132 Q 150 116, 150 86 L 150 44 Z" fill="url(#shieldGrad)" opacity="0.92" />
        <path d="M 120 38 L 96 49 L 96 84 Q 96 108, 120 122 Q 144 108, 144 84 L 144 49 Z" fill="white" opacity="0.92" />
        {/* Lock body */}
        <rect x="108" y="78" width="24" height="20" rx="4" fill="url(#shieldGrad)" />
        {/* Lock shackle */}
        <path d="M 113 78 L 113 71 Q 113 64, 120 64 Q 127 64, 127 71 L 127 78" fill="none" stroke="url(#shieldGrad)" strokeWidth="3" strokeLinecap="round" />
        {/* Keyhole */}
        <circle cx="120" cy="86" r="2.5" fill="white" />
        <rect x="119" y="86" width="2" height="6" fill="white" />
      </g>

      {/* Floating compliance badges */}
      <g className="aistk-feat-illu__float-b">
        <rect x="18" y="48" width="60" height="22" rx="11" fill="white" stroke="rgba(82,50,255,0.20)" />
        <circle cx="30" cy="59" r="5" fill="#5232FF" opacity="0.18" />
        <path d="M 27 59 L 29 61 L 33 57" stroke="#5232FF" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="40" y="63" fontSize="9" fontWeight="700" fill="#0051CC" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.iso_27001")}</text>
      </g>

      <g className="aistk-feat-illu__float-c">
        <rect x="162" y="36" width="56" height="22" rx="11" fill="white" stroke="rgba(82,50,255,0.20)" />
        <circle cx="174" cy="47" r="5" fill="#0077FF" opacity="0.18" />
        <path d="M 171 47 L 173 49 L 177 45" stroke="#0077FF" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="184" y="51" fontSize="9" fontWeight="700" fill="#0051CC" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.soc_2")}</text>
      </g>

      <g className="aistk-feat-illu__float-a">
        <rect x="30" y="120" width="48" height="22" rx="11" fill="white" stroke="rgba(82,50,255,0.20)" />
        <circle cx="42" cy="131" r="5" fill="#5232FF" opacity="0.18" />
        <path d="M 39 131 L 41 133 L 45 129" stroke="#5232FF" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="52" y="135" fontSize="9" fontWeight="700" fill="#0051CC" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.gdpr")}</text>
      </g>

      <g className="aistk-feat-illu__float-b">
        <rect x="160" y="124" width="56" height="22" rx="11" fill="white" stroke="rgba(82,50,255,0.20)" />
        <circle cx="172" cy="135" r="5" fill="#0077FF" opacity="0.18" />
        <path d="M 169 135 L 171 137 L 175 133" stroke="#0077FF" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="182" y="139" fontSize="9" fontWeight="700" fill="#0051CC" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.dpdp")}</text>
      </g>
    </svg>;
}

/* 3. Language Models — chat bubbles in different scripts */
function LanguageIllu() {
  const {
    t
  } = useTranslation();
  return <svg viewBox="0 0 240 180" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="bubGrad1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#B8CCFF" />
          <stop offset="100%" stopColor="#7E6BFF" />
        </linearGradient>
        <linearGradient id="bubGrad2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#9D8FFF" />
          <stop offset="100%" stopColor="#5232FF" />
        </linearGradient>
      </defs>

      {/* Central globe outline */}
      <g className="aistk-feat-illu__pulse">
        <circle cx="120" cy="90" r="44" fill="none" stroke="#5232FF" strokeWidth="0.8" opacity="0.25" />
        <circle cx="120" cy="90" r="32" fill="none" stroke="#0077FF" strokeWidth="0.6" opacity="0.18" />
      </g>

      <g className="aistk-feat-illu__rotate" style={{
      transformOrigin: '120px 90px'
    }}>
        <ellipse cx="120" cy="90" rx="36" ry="14" fill="none" stroke="#5232FF" strokeWidth="0.8" opacity="0.30" />
        <ellipse cx="120" cy="90" rx="14" ry="36" fill="none" stroke="#0077FF" strokeWidth="0.8" opacity="0.30" />
      </g>

      {/* Chat bubbles */}
      <g className="aistk-feat-illu__float-a">
        <path d="M 28 56 L 86 56 Q 94 56, 94 64 L 94 84 Q 94 92, 86 92 L 46 92 L 36 102 L 38 92 L 32 92 Q 28 92, 28 84 Z" fill="url(#bubGrad1)" opacity="0.92" />
        <text x="36" y="78" fontSize="11" fontWeight="700" fill="white" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.hello")}</text>
      </g>

      <g className="aistk-feat-illu__float-b">
        <path d="M 146 30 L 220 30 Q 228 30, 228 38 L 228 58 Q 228 66, 220 66 L 188 66 L 178 76 L 180 66 L 154 66 Q 146 66, 146 58 Z" fill="white" stroke="rgba(82,50,255,0.22)" />
        <text x="156" y="52" fontSize="11" fontWeight="700" fill="#5232FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.xfca3f39")}</text>
      </g>

      <g className="aistk-feat-illu__float-c">
        <path d="M 152 110 L 216 110 Q 224 110, 224 118 L 224 138 Q 224 146, 216 146 L 180 146 L 170 156 L 172 146 L 160 146 Q 152 146, 152 138 Z" fill="url(#bubGrad2)" opacity="0.85" />
        <text x="160" y="132" fontSize="11" fontWeight="700" fill="white" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.hola")}</text>
      </g>

      <g className="aistk-feat-illu__float-a">
        <path d="M 32 118 L 86 118 Q 94 118, 94 126 L 94 146 Q 94 154, 86 154 L 56 154 L 46 164 L 48 154 L 40 154 Q 32 154, 32 146 Z" fill="white" stroke="rgba(0,119,255,0.22)" />
        <text x="42" y="140" fontSize="11" fontWeight="700" fill="#0077FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.xb41cfe0")}</text>
      </g>
    </svg>;
}

/* 4. Intent & Emotion — pulsing emotion rings with heart core */
function IntentIllu() {
  const {
    t
  } = useTranslation();
  return <svg viewBox="0 0 240 180" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="heartGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FF8AB5" />
          <stop offset="100%" stopColor="#7E6BFF" />
        </linearGradient>
        <radialGradient id="emotionGlow">
          <stop offset="0%" stopColor="#FF8AB5" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#FF8AB5" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Outer glow */}
      <circle cx="120" cy="90" r="70" fill="url(#emotionGlow)" />

      {/* Pulsing rings */}
      <g className="aistk-feat-illu__pulse">
        <circle cx="120" cy="90" r="50" fill="none" stroke="#FF8AB5" strokeWidth="1" opacity="0.32" />
        <circle cx="120" cy="90" r="38" fill="none" stroke="#7E6BFF" strokeWidth="1" opacity="0.32" />
        <circle cx="120" cy="90" r="26" fill="none" stroke="#5232FF" strokeWidth="1" opacity="0.30" />
      </g>

      {/* Heart core */}
      <g className="aistk-feat-illu__float-a">
        <path d="M 120 110 L 100 92 Q 88 80, 100 70 Q 112 64, 120 78 Q 128 64, 140 70 Q 152 80, 140 92 Z" fill="url(#heartGrad)" />
        <path d="M 120 110 L 100 92 Q 88 80, 100 70 Q 112 64, 120 78 Q 128 64, 140 70 Q 152 80, 140 92 Z" fill="white" opacity="0.18" />
      </g>

      {/* Floating emotion tags */}
      <g className="aistk-feat-illu__float-b">
        <rect x="14" y="44" width="58" height="20" rx="10" fill="white" stroke="rgba(82,50,255,0.22)" />
        <circle cx="24" cy="54" r="3" fill="#FF6B6B" />
        <text x="33" y="58" fontSize="9" fontWeight="700" fill="#5232FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.urgency")}</text>
      </g>

      <g className="aistk-feat-illu__float-c">
        <rect x="168" y="40" width="58" height="20" rx="10" fill="white" stroke="rgba(82,50,255,0.22)" />
        <circle cx="178" cy="50" r="3" fill="#FF8AB5" />
        <text x="187" y="54" fontSize="9" fontWeight="700" fill="#5232FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.loyalty")}</text>
      </g>

      <g className="aistk-feat-illu__float-a">
        <rect x="20" y="118" width="50" height="20" rx="10" fill="white" stroke="rgba(82,50,255,0.22)" />
        <circle cx="30" cy="128" r="3" fill="#5232FF" />
        <text x="39" y="132" fontSize="9" fontWeight="700" fill="#5232FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.intent")}</text>
      </g>

      <g className="aistk-feat-illu__float-b">
        <rect x="174" y="124" width="52" height="20" rx="10" fill="white" stroke="rgba(82,50,255,0.22)" />
        <circle cx="184" cy="134" r="3" fill="#FF9D6B" />
        <text x="193" y="138" fontSize="9" fontWeight="700" fill="#5232FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.anger")}</text>
      </g>
    </svg>;
}

/* 5. Topics / Themes — radar scan with floating tag pills */
function TopicsIllu() {
  const {
    t
  } = useTranslation();
  return <svg viewBox="0 0 240 180" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="radarGrad">
          <stop offset="0%" stopColor="#7E6BFF" stopOpacity="0.4" />
          <stop offset="50%" stopColor="#5232FF" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#5232FF" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="topicSweep" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7E6BFF" stopOpacity="0" />
          <stop offset="100%" stopColor="#5232FF" stopOpacity="0.7" />
        </linearGradient>
      </defs>

      {/* Radar circles */}
      <g>
        <circle cx="120" cy="90" r="64" fill="url(#radarGrad)" />
        <circle cx="120" cy="90" r="60" fill="none" stroke="#5232FF" strokeWidth="0.8" opacity="0.20" />
        <circle cx="120" cy="90" r="42" fill="none" stroke="#5232FF" strokeWidth="0.8" opacity="0.25" />
        <circle cx="120" cy="90" r="24" fill="none" stroke="#5232FF" strokeWidth="0.8" opacity="0.30" />
        <circle cx="120" cy="90" r="6" fill="#5232FF" opacity="0.55" />
      </g>

      {/* Rotating sweep */}
      <g className="aistk-feat-illu__rotate" style={{
      transformOrigin: '120px 90px'
    }}>
        <path d="M 120 90 L 180 90 A 60 60 0 0 0 154 38 Z" fill="url(#topicSweep)" opacity="0.5" />
      </g>

      {/* Cross lines */}
      <line x1="56" y1="90" x2="184" y2="90" stroke="#5232FF" strokeWidth="0.6" opacity="0.18" strokeDasharray="2 4" />
      <line x1="120" y1="26" x2="120" y2="154" stroke="#5232FF" strokeWidth="0.6" opacity="0.18" strokeDasharray="2 4" />

      {/* Tag pills */}
      <g className="aistk-feat-illu__float-a">
        <rect x="14" y="34" width="60" height="20" rx="10" fill="white" stroke="rgba(82,50,255,0.22)" />
        <circle cx="22" cy="44" r="2.5" fill="#FF6B6B" />
        <text x="30" y="48" fontSize="9" fontWeight="700" fill="#5232FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.refund")}</text>
      </g>

      <g className="aistk-feat-illu__float-b">
        <rect x="170" y="46" width="62" height="20" rx="10" fill="white" stroke="rgba(82,50,255,0.22)" />
        <circle cx="178" cy="56" r="2.5" fill="#5232FF" />
        <text x="186" y="60" fontSize="9" fontWeight="700" fill="#5232FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.delivery")}</text>
      </g>

      <g className="aistk-feat-illu__float-c">
        <rect x="20" y="128" width="56" height="20" rx="10" fill="white" stroke="rgba(82,50,255,0.22)" />
        <circle cx="28" cy="138" r="2.5" fill="#0077FF" />
        <text x="36" y="142" fontSize="9" fontWeight="700" fill="#5232FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.praise")}</text>
      </g>

      <g className="aistk-feat-illu__float-a">
        <rect x="170" y="124" width="60" height="20" rx="10" fill="white" stroke="rgba(82,50,255,0.22)" />
        <circle cx="178" cy="134" r="2.5" fill="#FF9D6B" />
        <text x="186" y="138" fontSize="9" fontWeight="700" fill="#5232FF" fontFamily="Inter Tight, sans-serif">{t("aifeaturecards.outage")}</text>
      </g>
    </svg>;
}

/* 6. Generative + Predictive — sparkle wand + ascending chart */
function PredictiveIllu() {
  const {
    t
  } = useTranslation();
  return <svg viewBox="0 0 240 180" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="chartLine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7AA9FF" />
          <stop offset="100%" stopColor="#5232FF" />
        </linearGradient>
        <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7E6BFF" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#7E6BFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Ascending chart area */}
      <path d="M 20 150 L 20 130 L 50 116 L 80 122 L 110 96 L 140 88 L 170 64 L 200 46 L 220 38 L 220 150 Z" fill="url(#chartFill)" />
      <path d="M 20 130 L 50 116 L 80 122 L 110 96 L 140 88 L 170 64 L 200 46 L 220 38" fill="none" stroke="url(#chartLine)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />

      {/* Data points */}
      {[[50, 116], [80, 122], [110, 96], [140, 88], [170, 64], [200, 46]].map(([cx, cy], i) => <g key={i} className="aistk-feat-illu__pulse">
          <circle cx={cx} cy={cy} r="6" fill="white" opacity="0.6" />
          <circle cx={cx} cy={cy} r="3" fill="#5232FF" />
        </g>)}

      {/* Sparkle 1 */}
      <g className="aistk-feat-illu__float-a" transform="translate(40 40)">
        <path d="M 0 -10 L 2.5 -2.5 L 10 0 L 2.5 2.5 L 0 10 L -2.5 2.5 L -10 0 L -2.5 -2.5 Z" fill="#FF9D6B" opacity="0.9" />
      </g>

      {/* Sparkle 2 */}
      <g className="aistk-feat-illu__float-b" transform="translate(180 30)">
        <path d="M 0 -8 L 2 -2 L 8 0 L 2 2 L 0 8 L -2 2 L -8 0 L -2 -2 Z" fill="#5232FF" opacity="0.85" />
      </g>

      {/* Sparkle 3 */}
      <g className="aistk-feat-illu__float-c" transform="translate(150 110)">
        <path d="M 0 -6 L 1.5 -1.5 L 6 0 L 1.5 1.5 L 0 6 L -1.5 1.5 L -6 0 L -1.5 -1.5 Z" fill="#FF8AB5" opacity="0.9" />
      </g>

      {/* "AI" predictive badge top-right */}
      <g className="aistk-feat-illu__float-a">
        <rect x="186" y="60" width="44" height="22" rx="11" fill="white" stroke="rgba(82,50,255,0.22)" />
        <circle cx="196" cy="71" r="3" fill="#5232FF" />
        <text x="204" y="75" fontSize="9" fontWeight="800" fill="#5232FF" fontFamily="Inter Tight, sans-serif">+24%</text>
      </g>
    </svg>;
}